import React, { useCallback, useEffect, useState } from 'react';
import classnames from 'classnames';
import { useSelector } from 'react-redux';

import Icon from 'dpl/common/components/Icon';
import LoadingWrapper from 'dpl/components/LoadingWrapper';
import { isEmpty } from 'dpl/shared/utils/object';
import { scrollTo } from 'dpl/shared/utils';
import { sendAnalyticsEvent } from 'dpl/util/analytics';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_USER_JOURNEYS,
  ANALYTICS_VIEWS,
  ANALYTICS_VIEW_FEATURES,
  ANALYTICS_VIEW_FEATURE_LOCATIONS
} from 'dpl/constants/analytics';
import {
  PAYMENT_REQUEST_AMOUNT,
  PAYMENT_REQUEST_CATEGORY
} from 'dpl/constants/query_params';
import PaymentRequestAmountQuestion from 'dpl/breeder_dashboard/components/PaymentRequestAmountQuestion';
import StripeChargeModalAccountDataStepContainer from 'dpl/containers/StripeCharge/New/StripeChargeModalAccountDataStepContainer';
import StripeChargeModalHeader from 'dpl/components/StripeCharge/New/StripeChargeModal/StripeChargeModalHeader';
import { createLineItems } from 'dpl/components/StripeCharge/New/StripeChargeModal/util';
import {
  trackPaymentEvent,
  createPaymentProperties,
  trackPaymentConversion
} from 'dpl/breeder_dashboard/payments/utils/analytics';
import useCurrentUser from 'dpl/common/hooks/useCurrentUser';
import {
  PAYMENT_TIER_TYPE,
  SUBMIT_PAYMENT_LABEL
} from 'dpl/components/StripeCharge/New/StripeChargeModal/constants';
import FormContext from 'dpl/shared/form/utils/context';
import StripeChargeModalPaymentSummaryV2 from 'dpl/components/StripeCharge/New/StripeChargeModal/StripeChargeModalPaymentSummaryV2';
import { PAYMENT_REQUEST_CATEGORIES } from 'dpl/shared/constants/breeder_app';
import useForm from 'dpl/shared/form/hooks/useForm';
import useResourceQuery from 'dpl/shared/fetching/hooks/useResourceQuery';
import useResourceMutation from 'dpl/shared/fetching/hooks/useResourceMutation';
import { CONSTRAINT_TYPES } from 'dpl/shared/validations/constraints';
import { CompletePaymentRequestFormV1 } from 'dpl/components/StripeCharge/New/StripeChargeModal/CompletePaymentRequestFormV1';
import { CompletePaymentRequestFormV2 } from 'dpl/components/StripeCharge/New/StripeChargeModal/CompletePaymentRequestFormV2';
import isFeatureEnabled from 'dpl/util/isFeatureEnabled';
import { FEATURES } from 'dpl/shared/constants/features';
import { setupIntent } from 'dpl/payment_methods/resourceDefinitions';

import usePaymentRequest from '../hooks/usePaymentRequest';
import { breederProfileDataQueryDefinition } from '../utils/queryDefinitions';
import useCreatePaymentRequest from '../hooks/useCreatePaymentRequest';

const STEPS = {
  AMOUNT: 'AMOUNT',
  ACCOUNT_DATA: 'ACCOUNT_DATA',
  PAYMENT: 'PAYMENT'
};

function PaymentRequestModal() {
  const {
    category: initialCategory,
    amount: initialAmount,
    breederProfileId
  } = useSelector(state => ({
    category: state.queryParams[PAYMENT_REQUEST_CATEGORY],
    amount: state.queryParams[PAYMENT_REQUEST_AMOUNT],
    breederProfileId: state.server.breederProfileId
  }));

  const { value: breederProfile, isSuccess: breederProfileDataQuerySuccess } =
    useResourceQuery(breederProfileDataQueryDefinition(breederProfileId));
  const [setupIntentData, setSetupIntentData] = useState(null);

  const [paymentRequest, setPaymentRequest] = useState(null);
  const { value: userData } = useCurrentUser();
  const userId = userData?.data?.user_id;
  const [currentStep, setCurrentStep] = useState(STEPS.AMOUNT);
  const { createPaymentRequest } = useCreatePaymentRequest();
  const accountClaimed = userData?.data?.claimed;
  const buyerProfileId = userData?.data?.buyer_profile_id;

  const { mutateAsync: createSetupIntent } = useResourceMutation(setupIntent);

  useEffect(async () => {
    if (!userId) {
      return;
    }

    const { data } = await createSetupIntent();
    setSetupIntentData(data);
  }, [userId]);

  const ACHCheckEnabled = isFeatureEnabled(FEATURES.REL_ACH_BALANCE_REVIEW_V2);
  let CompletePaymentForm = null;
  let clientSecret = null;
  let completePaymentFormLoading = null;

  if (ACHCheckEnabled) {
    CompletePaymentForm = CompletePaymentRequestFormV2;
    clientSecret = setupIntentData?.setup_intent?.client_secret;
    completePaymentFormLoading = !setupIntentData;
  } else {
    CompletePaymentForm = CompletePaymentRequestFormV1;
    clientSecret = paymentRequest?.payment_intent?.client_secret;
    completePaymentFormLoading = !paymentRequest;
  }

  const {
    paymentRequestCategories,
    paymentRequestCategoryDataSuccess,
    paymentRequestUpdating,
    updatePaymentRequest
  } = usePaymentRequest({
    paymentRequestExternalId: paymentRequest?.payment_request?.external_id
  });

  const {
    addToFormState,
    batchSetValue,
    contextValue,
    formErrors,
    formState,
    handleFormFieldChange,
    reset
  } = useForm({
    defaultFormState: {
      ...paymentRequest?.payment_request,
      breeder_profile_id: breederProfileId,
      payment_tier: PAYMENT_TIER_TYPE.PROTECTED,
      amount_in_dollars:
        paymentRequest?.payment_request?.amount_in_cents / 100 ||
        Number(initialAmount) ||
        0,
      category: Object.values(PAYMENT_REQUEST_CATEGORIES).includes(
        initialCategory
      )
        ? initialCategory
        : ''
    },
    mapFormStateToValidationSchema: () => ({
      amount_in_dollars: CONSTRAINT_TYPES.NUMBER,
      category: CONSTRAINT_TYPES.TEXT
    })
  });

  // ! On page load, user's can click "Continue" without entering any inputs
  // ! because useForm.valid defaults to true. This forces the form validations
  // ! to run on page load to set useForm.valid to the correct state.
  useEffect(reset, []);

  const [isPaymentFeeCalculating, setIsPaymentFeeCalculating] = useState(false);

  async function updateOrCreatePaymentRequest() {
    const body = {
      payment_request: {
        ...formState,
        amount_in_cents: formState.amount_in_dollars * 100,
        breeder_profile_id: breederProfileId
      }
    };

    const func = paymentRequest ? updatePaymentRequest : createPaymentRequest;
    const response = await func({
      body
    });
    setPaymentRequest(response.data);

    return response.data;
  }

  async function goToPaymentStep() {
    setIsPaymentFeeCalculating(true);

    const paymentFeeResponse = await updateOrCreatePaymentRequest();

    trackPaymentEvent({
      paymentRequest: {
        category: formState.category,
        amount: formState.amount_in_dollars,
        initiated_by_buyer: true
      },
      paymentFee: paymentFeeResponse,
      userJourney: ANALYTICS_USER_JOURNEYS.PAYMENTS_BIP,
      buttonCta: 'continue',
      view: ANALYTICS_VIEWS.INPUT_PAYMENT_DETAILS_PAGE,
      viewFeature: ANALYTICS_VIEW_FEATURES.BUYER_INITIATED_PAYMENT,
      viewFeatureLocation:
        ANALYTICS_VIEW_FEATURE_LOCATIONS.BUYER_INITIATED_PAYMENT,
      paymentRequestId: paymentFeeResponse?.payment_request?.id
    });

    setIsPaymentFeeCalculating(false);
    setCurrentStep(STEPS.PAYMENT);
  }

  function handleBackClick() {
    // If the user is setting up their account for the first time, we want to make sure that they can go back and edit their account information
    if (currentStep === STEPS.PAYMENT && !accountClaimed) {
      setCurrentStep(STEPS.ACCOUNT_DATA);
    } else {
      setCurrentStep(STEPS.AMOUNT);
    }
  }

  useEffect(() => {
    scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }, [currentStep]);

  useEffect(() => {
    if (currentStep !== STEPS.PAYMENT) return;
    if (!formState.payment_tier && !formState.payment_method) return;
    updateOrCreatePaymentRequest();
  }, [formState.payment_tier, formState.payment_method]);

  useEffect(() => {
    if (paymentRequest?.payment_request) {
      batchSetValue({
        category: paymentRequest.payment_request.category,
        amount_in_dollars: paymentRequest.payment_request.amount_in_cents / 100,
        payment_tier: paymentRequest.payment_request.payment_tier
      });
    }
  }, [paymentRequest]);

  const lineItems = useCallback(() => {
    if (formState.category) {
      return createLineItems(
        paymentRequest.payment_request,
        formState.amount_in_dollars,
        formState.category
      );
    }

    return [];
  }, [
    paymentRequest?.payment_request,
    formState.amount_in_dollars,
    formState.category
  ]);

  function handleChangeTierSelection(tier) {
    if (tier === formState.payment_tier) {
      return;
    }

    addToFormState('payment_tier', tier);
  }

  function handleCompletePaymentRequestFormOnChange(stripeEvent) {
    addToFormState('payment_method', stripeEvent.value.type);
  }

  return (
    <FormContext.Provider value={contextValue}>
      <div
        className="StripeChargeModal relative"
        data-test-id="payment-request-modal"
      >
        <LoadingWrapper
          isLoading={
            !(
              breederProfileDataQuerySuccess &&
              paymentRequestCategoryDataSuccess
            )
          }
          className="transform-center-all"
        >
          <div className="flex flex-column">
            <div className="StripeChargeModal__container flex-auto bg-white mb20-lg mb10-sm shadow-2-ns br2-ns mw-100 center relative">
              {currentStep !== STEPS.AMOUNT && (
                <button
                  type="button"
                  className="absolute left-0 top-0 padding pl6 pt6 black-50 z-1"
                  onClick={handleBackClick}
                >
                  <Icon name="lined-arrow-left" />
                </button>
              )}

              <StripeChargeModalHeader
                breederProfile={breederProfile}
                subtotalInDollars={formState.amount_in_dollars}
                excludePuppyCard={currentStep === STEPS.AMOUNT}
                paymentCategory={formState.category}
                dog={paymentRequest?.dog}
              />

              {currentStep === STEPS.AMOUNT && (
                <div className="pb8 ph20-md ph6 bb b--light-gray">
                  <PaymentRequestAmountQuestion
                    amountInDollars={formState.amount_in_dollars}
                    autoFocus
                    availableCategories={paymentRequestCategories}
                    category={formState.category}
                    isBuyerRequest
                    onChange={handleFormFieldChange}
                    shouldShowEditButton={Boolean(Number(initialAmount))}
                  />
                  <button
                    disabled={!isEmpty(formErrors)}
                    type="button"
                    className={classnames(
                      'button button--primary button--large w-100 flex items-center fw-medium',
                      { 'button--loading': isPaymentFeeCalculating }
                    )}
                    onClick={() => {
                      accountClaimed
                        ? goToPaymentStep()
                        : setCurrentStep(STEPS.ACCOUNT_DATA);
                    }}
                  >
                    <span className="center">Continue</span>
                    <Icon name="lined-arrow-right" />
                  </button>
                </div>
              )}
              {currentStep === STEPS.ACCOUNT_DATA && (
                <StripeChargeModalAccountDataStepContainer
                  onSubmitSuccess={goToPaymentStep}
                />
              )}
              {currentStep === STEPS.PAYMENT && (
                <LoadingWrapper
                  isLoading={completePaymentFormLoading}
                  className="transform-center-all"
                >
                  <CompletePaymentForm
                    className="mt3 mh6 mh20-sm mb8"
                    clientSecret={clientSecret}
                    externalId={paymentRequest.payment_request.external_id}
                    id={paymentRequest.payment_request.id}
                    breederProfileId={breederProfileId}
                    buyerProfileId={buyerProfileId}
                    paymentCategory={formState.category}
                    isFetching={paymentRequestUpdating}
                    onChange={handleCompletePaymentRequestFormOnChange}
                    onSubmit={async () => {
                      sendAnalyticsEvent(ANALYTICS_EVENTS.BUTTON_CLICKED, {
                        ...createPaymentProperties(
                          {
                            ...paymentRequest.payment_request,
                            category: formState.category,
                            amount: formState.amount_in_dollars,
                            initiated_by_buyer: true,
                            payment_method: formState.payment_method
                          },
                          paymentRequest.payment_request
                        ),
                        button_cta: SUBMIT_PAYMENT_LABEL,
                        user_journey: ANALYTICS_USER_JOURNEYS.PAYMENTS_BIP,
                        view: ANALYTICS_VIEWS.PAYMENT_DETAILS,
                        view_feature:
                          ANALYTICS_VIEW_FEATURES.PAYMENT_CHECKOUT_PAGE,
                        view_feature_location:
                          ANALYTICS_VIEW_FEATURE_LOCATIONS.PAYMENT_CHECKOUT_PAGE,
                        payment_request_id: paymentRequest.payment_request.id
                      });

                      await trackPaymentConversion(
                        formState.category,
                        formState.amount_in_dollars
                      );
                    }}
                  >
                    <LoadingWrapper isLoading={paymentRequestUpdating}>
                      <StripeChargeModalPaymentSummaryV2
                        amountTotal={
                          paymentRequest.payment_request.total_in_dollars
                        }
                        amountSubtotal={formState.amount_in_dollars}
                        lineItems={lineItems()}
                        paymentTier={formState.payment_tier}
                        paymentMethod={formState.payment_method}
                        category={formState.category}
                        onChangeSelection={handleChangeTierSelection}
                      />
                    </LoadingWrapper>
                  </CompletePaymentForm>
                </LoadingWrapper>
              )}
            </div>
          </div>
        </LoadingWrapper>
      </div>
    </FormContext.Provider>
  );
}

export default PaymentRequestModal;
